import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonAvatar,
  IonLabel,
  IonNote,
  IonCheckbox,
  IonAlert,
  IonSpinner,
  useIonToast,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useMutation } from '@apollo/client/react';
import { useState, useCallback } from 'react';
import { UPDATE_LEAGUE_ADMINS } from '../../../../graphql/mutations/leagueAdmin';
import { GET_FANTASY_LEAGUE } from '../../../../graphql/queries/fantasyLeague';
import { withAvatarCacheBuster } from '../../../../lib/avatarUrl';
import type {
  UpdateLeagueAdminsMutation,
  UpdateLeagueAdminsMutationVariables,
  GetFantasyLeagueQuery,
} from '../../../../types/graphql-generated';

type Member = NonNullable<GetFantasyLeagueQuery['fantasyLeague']>['members'][number];

interface ManageAdminsModalProps {
  isOpen: boolean;
  leagueId: string;
  members: Member[];
  currentMemberId?: string;
  currentMemberRole?: string;
  onClose: () => void;
}

export const ManageAdminsModal = ({
  isOpen,
  leagueId,
  members,
  currentMemberId,
  currentMemberRole,
  onClose,
}: ManageAdminsModalProps) => {
  const [presentToast] = useIonToast();
  const [showConfirm, setShowConfirm] = useState(false);
  // State is initialized once on mount. The parent passes key={showManageAdmins}
  // to force re-mount each time the modal opens, ensuring fresh state from members.
  const [selectedAdminIds, setSelectedAdminIds] = useState<Set<string>>(
    () => new Set(members.filter((m) => m.role === 'admin' || m.role === 'creator').map((m) => m.id))
  );

  const [updateAdmins, { loading }] = useMutation<
    UpdateLeagueAdminsMutation,
    UpdateLeagueAdminsMutationVariables
  >(UPDATE_LEAGUE_ADMINS, {
    refetchQueries: [{ query: GET_FANTASY_LEAGUE, variables: { id: leagueId } }],
    onCompleted: (res) => {
      const errors = res.updateLeagueAdmins?.errors ?? [];
      if (errors.length === 0) {
        presentToast({
          message: 'Administradores actualizados correctamente',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-success',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
        onClose();
      } else {
        presentToast({
          message: errors[0] ?? 'Error al actualizar los administradores',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-danger',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
      }
    },
    onError: (error) => {
      presentToast({
        message: `Error: ${error.message}`,
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'app-footer',
        cssClass: 'toast-danger',
        buttons: [{ icon: closeOutline, role: 'cancel' }],
      });
    },
  });

  const handleToggle = useCallback((memberId: string, checked: boolean) => {
    setSelectedAdminIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(memberId);
      } else {
        next.delete(memberId);
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(() => {
    // Frontend guardrail: prevent the only admin from demoting themselves
    if (
      currentMemberRole === 'admin' &&
      currentMemberId !== undefined &&
      !selectedAdminIds.has(currentMemberId)
    ) {
      const otherPrivileged = members.some(
        (m) => m.id !== currentMemberId && (m.role === 'admin' || m.role === 'creator')
      );

      if (!otherPrivileged) {
        presentToast({
          message:
            'No puedes quitarte a ti mismo los permisos de administrador si eres el único administrador de la liga',
          duration: 4000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-danger',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
        return;
      }
    }

    setShowConfirm(true);
  }, [currentMemberId, currentMemberRole, members, selectedAdminIds, presentToast]);

  const handleConfirmSave = useCallback(() => {
    // Only send non-creator member IDs; the backend skips creators anyway
    const adminMemberIds = Array.from(selectedAdminIds).filter((id) => {
      const member = members.find((m) => m.id === id);
      return member?.role !== 'creator';
    });

    updateAdmins({
      variables: { input: { fantasyLeagueId: leagueId, adminMemberIds } },
    });
    setShowConfirm(false);
  }, [updateAdmins, leagueId, selectedAdminIds, members]);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Gestionar administradores</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>Cerrar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        ) : (
          <>
            <IonList>
              {members.map((member) => {
                const isCreator = member.role === 'creator';
                const isChecked = selectedAdminIds.has(member.id);

                return (
                  <IonItem key={member.id}>
                    <IonAvatar slot="start" className="me-3">
                      {member.user.picture ? (
                        <img
                          src={withAvatarCacheBuster(member.user.picture, member.user.updatedAt) ?? undefined}
                          alt={`Foto de ${member.user.fullName ?? 'participante'}`}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center rounded-full">
                          <span className="text-white font-bold text-sm">
                            {(member.user.fullName ?? '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </IonAvatar>
                    <IonLabel>
                      <h2 className="font-semibold">{member.user.fullName ?? 'Participante'}</h2>
                      {member.teamName ? <IonNote>{member.teamName}</IonNote> : null}
                      {isCreator ? (
                        <IonNote color="medium" className="block text-xs">
                          Creador
                        </IonNote>
                      ) : null}
                    </IonLabel>
                    <IonCheckbox
                      slot="end"
                      checked={isChecked}
                      disabled={isCreator}
                      aria-label={`Hacer administrador a ${member.user.fullName ?? 'participante'}`}
                      onIonChange={(e) => handleToggle(member.id, e.detail.checked)}
                    />
                  </IonItem>
                );
              })}
            </IonList>

            <div className="mt-4">
              <IonButton expand="block" onClick={handleSave}>
                Guardar
              </IonButton>
            </div>
          </>
        )}

        <IonAlert
          isOpen={showConfirm}
          onDidDismiss={() => setShowConfirm(false)}
          header="Gestionar administradores"
          message="¿Confirmas los cambios en los permisos de administrador?"
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Guardar', role: 'confirm', handler: handleConfirmSave },
          ]}
        />
      </IonContent>
    </IonModal>
  );
};
